import * as Msdyn365 from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult, AttributeValue, Customer } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ICheckoutBillingAddressViewProps } from '@msdyn365-commerce-modules/address';
import { GetaddressesInput, getCustomerAddressesAction } from '../../actions/GetCustomerAddress.action';
import { ICheckoutBillingAddressData } from '@msdyn365-commerce-modules/address/dist/types/modules/checkout-billing-address';
import { IAddressEntity } from '../../actions/SearchItemsDataServiceEntities.g';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
interface ICheckoutBillingAddressDataExtended extends ICheckoutBillingAddressData {
    customerInformation?: AsyncResult<Customer>;
}

const CheckoutBillingAddressView: React.FC<ICheckoutBillingAddressViewProps> = props => {
    const { customerInformation } = props.data as ICheckoutBillingAddressDataExtended;
    const [invoiceAddresses, setInvoiceAddresses] = React.useState<IAddressEntity[]>([]);
    const [selectedAddress, setSelectedAddress] = React.useState<IAddressEntity | undefined>(undefined);
    const [editingAddress, setEditingAddress] = React.useState<IAddressEntity | undefined>(undefined);
    const [isEditing, setIsEditing] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const billingAddressRecIdName = props.context.app.config.billingAddressRecIdName || 'BillingAddressRecId';

    const fetchAddresses = async () => {
        if (customerInformation) {
            const customerAddressInput = new GetaddressesInput((await customerInformation).InvoiceAccount || '');
            const allOrganizationAddresses = await getCustomerAddressesAction(customerAddressInput, props.context.actionContext);
            return allOrganizationAddresses?.Addresses?.filter(orgAddress => orgAddress.Purpose === 'Invoice') || [];
        }
        return [];
    };

    React.useEffect(() => {
        const loadAddresses = async () => {
            setIsLoading(true);
            const checkoutState = props.data.checkout.result;
            if (checkoutState) {
                await checkoutState.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
            }

            try {
                const checkoutState = await props.data.checkout;

                if (checkoutState) {
                    const cart = checkoutState?.checkoutCart.cart;
                    const checkoutAttributes = cart?.AttributeValues as AttributeValue[];
                    const billingRecAttributeText = checkoutAttributes?.find(attr => attr.Name === billingAddressRecIdName);
                    const fetchedAddresses = await fetchAddresses();

                    if (fetchedAddresses.length > 0) {
                        if (checkoutState && billingRecAttributeText) {
                            const selectedAddress =
                                fetchedAddresses.find(
                                    address => address.LogisticsLocationRecordId.toString() === billingRecAttributeText.TextValue
                                ) || fetchedAddresses[0];

                            setSelectedAddress(selectedAddress);
                        } else {
                            setSelectedAddress(fetchedAddresses[0]);
                        }
                    }
                    setInvoiceAddresses(fetchedAddresses);
                }
            } catch (error) {
                console.error('Error loading addresses:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadAddresses();
        props.moduleState.onPending();
    }, [customerInformation, props.data.checkout]);

    React.useEffect(() => {
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            const checkAndUpdateClasses = () => {
                const billingAddress = props.moduleState.getModuleByTypeName('bc-checkout-billing-address');
                if (billingAddress) {
                    const element = document.querySelector(`[class*='checkout-billing-address']`);
                    const parentElement = element && element.closest('.ms-checkout__guided-card');
                    if (parentElement) {
                        if (!parentElement.classList.contains('billing-address-section')) {
                            parentElement.classList.add('billing-address-section');
                        }
                        if (!isLoading && invoiceAddresses.length <= 0 && !parentElement.classList.contains('no-invoice')) {
                            parentElement.classList.add('no-invoice');
                        }
                    }
                }
            };

            // Perform the initial check
            checkAndUpdateClasses();

            // MutationObserver to watch for changes in the DOM
            const observer = new MutationObserver(mutations => {
                mutations.forEach(() => {
                    checkAndUpdateClasses();
                });
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Cleanup observer on unmount
            return () => {
                observer.disconnect();
            };
        }
        return undefined;
    }, [props.friendlyName, isLoading]);

    React.useEffect(() => {
        if (!isLoading && invoiceAddresses.length <= 0) {
            props.moduleState.onReady();
        }
    }, [invoiceAddresses.length, isLoading]);

    const onSubmit = async (address: IAddressEntity | undefined): Promise<void> => {
        const checkoutState = await props.data.checkout;
        const cart = checkoutState.checkoutCart.cart;
        const checkoutAttributes = cart.AttributeValues as AttributeValue[];
        const billingRecAttributeIndex = checkoutAttributes?.findIndex(attr => attr.Name === billingAddressRecIdName);
        if (billingRecAttributeIndex >= 0) {
            checkoutAttributes[billingRecAttributeIndex].TextValue = address?.LogisticsLocationRecordId.toString();
        } else {
            const attributeObj = {
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: billingAddressRecIdName,
                TextValue: address?.LogisticsLocationRecordId.toString(),
                TextValueTranslations: []
            };
            checkoutAttributes.push(attributeObj);
        }
        await checkoutState.checkoutCart.updateAttributeValues({
            newAttributeValues: checkoutAttributes
        });
        await checkoutState.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
        const updatedCheckoutState = await props.data.checkout;
        await updateAsync({ callerContext: props.context.actionContext }, updatedCheckoutState.checkoutCart.cart);
        await checkoutState.checkoutCart.refreshCart({});
    };

    const EditButton: React.FC = () => {
        const handleEditClick = () => {
            setEditingAddress(selectedAddress);
            setIsEditing(true);
            props.moduleState.onUpdating();
            const elements = document.querySelectorAll('.ms-checkout__guided-card .ms-checkout__guided-card-btn-edit');
            elements.forEach(element => {
                const ariaLabel = element?.getAttribute('aria-label');
                if (ariaLabel && ariaLabel.toLowerCase().includes(props.friendlyName?.toLowerCase() || '')) {
                    const parentElement = element.closest('.ms-checkout__guided-card');
                    if (parentElement) {
                        parentElement.classList.add('billing-address-section');
                    }
                }
            });
        };
        return (
            <>
                {!isLoading && invoiceAddresses.length > 0 ? (
                    <button
                        type='button'
                        onClick={handleEditClick}
                        className='ms-checkout__guided-card-btn-edit msc-btn btn-link billing-address-change-btn'
                        title='Change'
                        role='Button'
                        aria-label={`Change ${props.friendlyName}`}
                        data-exp-event-id='Checkout.Checkout.Change.click'
                    >
                        Change
                    </button>
                ) : (
                    <></>
                )}
            </>
        );
    };

    const SaveButton: React.FC = () => {
        const handleSaveClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault();
            setSelectedAddress(editingAddress);
            setIsEditing(false);
            props.moduleState.onReady();
            await onSubmit(editingAddress);
        };
        return (
            <button
                type='button'
                title='Save'
                onClick={handleSaveClick}
                data-exp-event-id='Checkout.Checkout.Save.click'
                className='ms-checkout__guided-card-btn-save msc-btn btn-primary'
            >
                Save
            </button>
        );
    };

    const CancelButton: React.FC = () => {
        const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault();
            setEditingAddress(undefined);
            setIsEditing(false);
            props.moduleState.onReady();
        };
        return (
            <button
                type='button'
                title='Cancel'
                onClick={handleCancelClick}
                data-exp-event-id='Checkout.Checkout.Cancel.click'
                className='ms-checkout__guided-card-btn-cancel msc-btn btn-secondary'
            >
                Cancel
            </button>
        );
    };

    const ContinueButton: React.FC = () => {
        const handleContinueClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault();
            props.moduleState.onReady();
            await onSubmit(selectedAddress);
        };
        return (
            <button
                type='button'
                title='Save & continue'
                onClick={handleContinueClick}
                data-exp-event-id='Checkout.Checkout.Save and Continue.click'
                className='ms-checkout__guided-card-btn-save msc-btn btn-primary'
            >
                Save & continue
            </button>
        );
    };

    const handleRadioChange = (address: IAddressEntity) => {
        setEditingAddress(address);
    };

    const { CheckoutBillingAddress, checkoutErrorRef } = props;
    const parentClassName = 'msc-address-detail';
    const itemClassName = `${parentClassName}__item`;
    const combinedItemClassName = `${itemClassName} ${itemClassName}-newline`;
    const parentClassNameSelect = 'msc-address-select';
    return (
        <Module {...CheckoutBillingAddress} ref={checkoutErrorRef}>
            {/* ITS - OOTB Code */}
            {/* {heading} */}
            <>
                {isLoading ? (
                    <p>Loading Invoice Billing addresses...</p>
                ) : (
                    <>
                        {isEditing ? (
                            <Node className={parentClassNameSelect}>
                                <div className={`${parentClassNameSelect}__wrapper`}>
                                    {invoiceAddresses.map(address => (
                                        <div className={`${parentClassNameSelect}__item`} key={address.RecordId}>
                                            <input
                                                type='radio'
                                                name='selectAddressOptions'
                                                value={address.RecordId}
                                                checked={editingAddress?.RecordId === address.RecordId}
                                                onChange={() => handleRadioChange(address)}
                                                className='msc-address-select__input msc-address-select__input-radio'
                                                aria-label='Select address Service'
                                                data-exp-event-id='Checkout.Billing address.selectAddressOptions.click'
                                            />
                                            <div className={parentClassName}>
                                                <span className={`${combinedItemClassName} ${itemClassName}-name`}>
                                                    {address.Description}
                                                </span>
                                                <span className={`${combinedItemClassName} ${itemClassName}-street`}>{address.Street}</span>
                                                <span className={`${itemClassName} ${itemClassName}-city`}>{address.City}</span>
                                                <span className={`${itemClassName} ${itemClassName}-state`}>{address.State}</span>
                                                <span className={`${combinedItemClassName} ${itemClassName}-zipcode`}>
                                                    {address.ZipCode}
                                                </span>
                                                <span className={`${itemClassName} ${itemClassName}-threeletterisoregionname`}>
                                                    {address.ThreeLetterISORegionName}
                                                </span>
                                                <span className={`${itemClassName} ${itemClassName}-address-detail_Phone`}>
                                                    {address.Phone}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className={`${parentClassName}__buttons`}>
                                    <SaveButton />
                                    <CancelButton />
                                </div>
                            </Node>
                        ) : (
                            <Node className={parentClassName}>
                                {selectedAddress && (
                                    <>
                                        <span className={`${combinedItemClassName} ${itemClassName}-name`}>
                                            {selectedAddress.Description}
                                        </span>
                                        <span className={`${combinedItemClassName} ${itemClassName}-street`}>{selectedAddress.Street}</span>
                                        <span className={`${itemClassName} ${itemClassName}-city`}>{selectedAddress.City}</span>
                                        <span className={`${itemClassName} ${itemClassName}-state`}>{selectedAddress.State}</span>
                                        <span className={`${combinedItemClassName} ${itemClassName}-zipcode`}>
                                            {selectedAddress.ZipCode}
                                        </span>
                                        <span className={`${itemClassName} ${itemClassName}-threeletterisoregionname`}>
                                            {selectedAddress.ThreeLetterISORegionName}
                                        </span>
                                        <span className={`${itemClassName} ${itemClassName}-address-detail_Phone`}>
                                            {selectedAddress.Phone}
                                        </span>
                                        {props.moduleState.isPending ? (
                                            <div className={`${parentClassName}__buttons`}>
                                                <ContinueButton />
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                    </>
                                )}
                                <EditButton />
                            </Node>
                        )}
                    </>
                )}
            </>
        </Module>
    );
};

export default CheckoutBillingAddressView;
